// src/ui/MenuUIUtils.js - Consolidated & Enhanced UI Utilities v2 (with Backgrounds)
import Phaser from 'phaser';

class MenuUIUtils {
  // Standardized Colors (Match CSS Variables where possible)
  static COLORS = {
    primary: 0x00FF00,     // Neon Green
    primary_dark: 0x00BB00,
    primary_light: 0x66FF66,
    secondary: 0x00FFFF,   // Cyan
    secondary_dark: 0x00BBBB,
    secondary_light: 0x66FFFF,
    danger: 0xFF3333,      // Red
    warning: 0xFFCC00,     // Yellow
    success: 0x00CC00,     // Brighter Green
    accent1: 0xFFAA00,     // Orange (Slide Wars Theme)
    accent2: 0x0099FF,     // Blue (PPT Hero Theme)
    accent3: 0xAA00FF,     // Purple (Admin/Highlight)
    accent4: 0xCCFF00,     // Lime Green/Yellow (Classify Tech Theme) <-- NEW
    textLight: 0xFFFFFF,
    textMedium: 0xCCCCCC,
    textDark: 0x888888,
    bgDark: 0x000000,
    bgMedium: 0x111111,    // Very Dark Grey
    bgLight: 0x222222,     // Dark Grey
    bgOverlay: 0x000000,   // Solid Black for Phaser overlay backgrounds
    border: 0x00FF00,      // Primary Green border
    borderMedium: 0x444444,
    borderLight: 0x666666,
    info: 0x00FFFF,        // Cyan for info
  };

  // Standardized Fonts (Match CSS Variables)
  static FONTS = {
    family: 'monospace',
    sizeSm: '16px',
    sizeMd: '20px',
    sizeLg: '26px',
    sizeXl: '32px',
    sizeTitle: '48px',
  };

  // Standardized Spacing (Match CSS Variables)
  static SPACING = {
    xs: 4, sm: 8, md: 16, lg: 24, xl: 32
  };

  static isMobile() {
    // Basic check, refine if needed
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /** Creates a standard themed text object. */
  static createThemedText(scene, x, y, text, sizeKey = 'sizeMd', colorHex = this.COLORS.textLight, origin = 0.5) {
    if (!scene || !scene.add) {
        console.error("Invalid scene passed to createThemedText");
        return null;
    }
    try {
      const fontSize = typeof sizeKey === 'string' ? (this.FONTS[sizeKey] || this.FONTS.sizeMd) : `${sizeKey}px`;
      const style = {
        fontFamily: this.FONTS.family,
        fontSize: fontSize,
        color: Phaser.Display.Color.ValueToColor(colorHex).rgba, // Use Phaser color conversion
        align: 'center',
      };
      const textObj = scene.add.text(x, y, text, style).setOrigin(origin);
      return textObj;
    } catch (e) { console.error("Error createThemedText:", e); return null; }
  }

  /** Creates an animated title. */
  static createTitle(scene, text, y, options = {}) {
    if (!scene || !scene.add || !scene.cameras?.main) {
        console.error("Invalid scene or camera passed to createTitle");
        return { setPosition: () => {}, y: y || 0, active: false }; // Return dummy object
    }
    try {
      const centerX = scene.cameras.main.width / 2;
      const finalY = y || scene.cameras.main.height * 0.15; // Default Y position
      const size = options.size || this.FONTS.sizeTitle;
      const color = options.color || this.COLORS.primary;

      const titleText = scene.add.text(centerX, finalY, text, {
        fontFamily: this.FONTS.family,
        fontSize: size,
        color: Phaser.Display.Color.ValueToColor(color).rgba,
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 5, fill: true }
      }).setOrigin(0.5).setDepth(10);

      // Subtle pulse animation
      if (scene.tweens) {
        scene.tweens.add({
          targets: titleText,
          scale: { from: 1, to: 1.02 },
          duration: 2000,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1
        });
      }
      return titleText;
    } catch (e) {
        console.error("Error createTitle:", e);
        return { setPosition: () => {}, y: y || 0, active: false }; // Return dummy object
    }
  }

  
  /**
   * Creates a styled button container with background, text, and hover effects
   * @param {Phaser.Scene} scene - Scene to add the button to
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {string} label - Button text
   * @param {Function} callback - Click handler
   * @param {Object} options - Button options
   * @returns {Phaser.GameObjects.Container} Button container
   */
   // Also update createStyledButton to accept options object directly
// src/ui/MenuUIUtils.js

// Replace the entire createStyledButton method with this:
// src/ui/MenuUIUtils.js

// Replace the entire createStyledButton method with this:
static createStyledButton(scene, x, y, label, callback, options = {}) {
  // --- Refined Scene Validation ---
  if (!scene || !scene.sys || !scene.add || typeof scene.add.container !== 'function' || typeof scene.add.graphics !== 'function' || typeof scene.add.text !== 'function') {
      console.error(`[MenuUIUtils] Invalid or incomplete scene context passed to createStyledButton for label "${label}". Scene Key: ${scene?.scene?.key}. Cannot create button.`);
      if(scene && scene.sys) console.error(`  Scene Status: ${scene.sys.settings.status} (${Phaser.Scenes.RUNNING === scene.sys.settings.status ? 'Running' : 'Not Running'})`);
      return null;
  }
  // --- END Refined Scene Validation ---

  try {
    // Mobile-optimized sizing
    const isMobile = this.isMobile();
    const width = options.width || (isMobile ? 240 : 180);
    const height = options.height || (isMobile ? 56 : 40); // Larger touch targets on mobile
    const bgColor = options.bgColor !== undefined ? options.bgColor : this.COLORS.primary;
    const textColor = options.textColor !== undefined ? options.textColor : this.COLORS.bgDark;
    const fontSize = options.fontSize || (isMobile ? this.FONTS.sizeLg : this.FONTS.sizeMd); // Larger text on mobile
    const cornerRadius = options.radius !== undefined ? options.radius : (isMobile ? this.SPACING.md : this.SPACING.xs);
    
    const bgColorObj = Phaser.Display.Color.ValueToColor(bgColor);
    const hoverColor = options.hoverColor !== undefined ? options.hoverColor : bgColorObj.lighten(15).color;
    const clickColor = options.clickColor !== undefined ? options.clickColor : bgColorObj.darken(10).color;

    const textStyle = {
      fontFamily: this.FONTS.family,
      fontSize: fontSize,
      color: Phaser.Display.Color.ValueToColor(textColor).rgba,
      fontStyle: 'bold',
      align: 'center'
    };

    const container = scene.add.container(x, y);
    container.setSize(width, height);

    const bg = scene.add.graphics();
    bg.fillStyle(bgColorObj.color, bgColorObj.alphaGL);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
    container.add(bg);

    const text = scene.add.text(0, 0, label, textStyle).setOrigin(0.5);
    container.add(text);

    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      if (!container.active || !bg.active || !container.input?.enabled) return;
      bg.clear().fillStyle(hoverColor, 1).fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
      const scaleAmount = this.isMobile() ? 1.05 : 1.03; // Larger scale on mobile for better feedback
      scene.tweens?.add({ targets: container, scale: scaleAmount, duration: 100, ease: 'Sine.easeOut' });
    });

    container.on('pointerout', () => {
      if (!container.active || !bg.active) return;
      bg.clear().fillStyle(bgColorObj.color, bgColorObj.alphaGL).fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
      scene.tweens?.add({ targets: container, scale: 1, duration: 100, ease: 'Sine.easeIn' });
    });

    container.on('pointerdown', () => {
      if (!container.active || !bg.active || !container.input?.enabled) return;
      bg.clear().fillStyle(clickColor, 1).fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
      
      // Enhanced mobile feedback
      const scaleAmount = this.isMobile() ? 0.93 : 0.97; // More pronounced feedback on mobile
      scene.tweens?.add({ targets: container, scale: scaleAmount, duration: 50, ease: 'Sine.easeOut' });
      
      // Add haptic feedback on mobile
      if (this.isMobile() && 'vibrate' in navigator) {
        navigator.vibrate([10]);
      }
      scene.tweens?.add({ targets: container, scale: 0.97, duration: 80, ease: 'Sine.easeOut' });
    });

    container.on('pointerup', (pointer) => {
        if (!container.active || !container.input?.enabled || !container.scene?.scene?.isActive(container.scene.scene.key)) {
          return;
        }
        const worldPoint = scene.input.activePointer;
        if (container.getBounds().contains(worldPoint.worldX, worldPoint.worldY)) {
            if (bg.active) bg.clear().fillStyle(hoverColor, 1).fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
            scene.tweens?.add({ targets: container, scale: 1.03, duration: 80, ease: 'Sine.easeIn' });
            if (callback) {
                try { callback(); } catch (error) {
                    console.error(`Button callback error for "${label}":`, error);
                    if (scene.showMessage && typeof scene.showMessage === 'function') {
                        scene.showMessage(`Error: ${error.message}`, 2000, true);
                    }
                }
            }
        } else {
            if (bg.active) bg.clear().fillStyle(bgColorObj.color, bgColorObj.alphaGL).fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
            container.setScale(1);
        }
    });

    container.setData({
        rectGraphic: bg, textObject: text, originalColor: bgColor,
        label: label, intendedWidth: width, intendedHeight: height,
        textColor: textColor
    });

    return container;
  } catch (e) {
    console.error(`Error createStyledButton for label "${label}" in scene ${scene?.scene?.key}:`, e);
    if (e.message.includes("is not a function")) {
         console.error("Scene Context:", scene);
         console.error("Scene Add Factory:", scene?.add);
    }
    return null;
  }
}

  /** Sets a button to loading state */
  static setButtonLoading(buttonContainer, loadingText = "Loading...", disable = true) {
    if (!buttonContainer || !buttonContainer.active) return;

    try {
      const bg = buttonContainer.getData?.('rectGraphic');
      const text = buttonContainer.getData?.('textObject');
      const width = buttonContainer.width;
      const height = buttonContainer.height;
      const cornerRadius = this.SPACING.xs; // Assuming default radius

      if (bg && typeof bg.clear === 'function') {
        bg.clear().fillStyle(this.COLORS.bgLight, 0.7);
        bg.fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
      }

      if (text) {
        text.setText(loadingText).setColor(Phaser.Display.Color.ValueToColor(this.COLORS.textDark).rgba);
      }

      if (disable && buttonContainer.input) {
        buttonContainer.disableInteractive();
         buttonContainer.input.useHandCursor = false; // Remove hand cursor
      }
    } catch (e) {
      console.error("Error in setButtonLoading:", e);
    }
  }

  /** Reset button state to normal */
  static resetButtonState(buttonContainer, defaultText = null, defaultColor = null) {
    if (!buttonContainer || !buttonContainer.active) return;

    try {
      const bg = buttonContainer.getData?.('rectGraphic');
      const text = buttonContainer.getData?.('textObject');
      const originalColor = buttonContainer.getData?.('originalColor');
      const originalLabel = buttonContainer.getData?.('label');
      const width = buttonContainer.width;
      const height = buttonContainer.height;
      const cornerRadius = this.SPACING.xs; // Assuming default radius

      const finalColor = defaultColor !== null ? defaultColor : (originalColor !== undefined ? originalColor : this.COLORS.primary);
      const finalText = defaultText !== null ? defaultText : (originalLabel !== undefined ? originalLabel : 'Submit');

      if (bg && typeof bg.clear === 'function') {
        bg.clear().fillStyle(finalColor, 1);
        bg.fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
      }

      if (text) {
        text.setText(finalText).setColor(Phaser.Display.Color.ValueToColor(this.COLORS.bgDark).rgba);
      }

      buttonContainer.setInteractive({ useHandCursor: true }); // Re-enable
    } catch (e) {
      console.error("Error in resetButtonState:", e);
    }
  }


  /** Creates a standard interactive menu item (Text only). */
  static createMenuItem(scene, x, y, text, callback, options = {}) {
     if (!scene || !scene.add) {
        console.error("Invalid scene passed to createMenuItem");
        return null;
     }
     try {
       const color = options.fill || this.COLORS.primary;
       const size = options.size || this.FONTS.sizeLg;
       const hoverColor = options.hoverFill || this.COLORS.textLight; // White hover
       const shadowColor = options.shadowColor || Phaser.Display.Color.ValueToColor(color).rgba.replace('1)', '0.5)'); // Semi-transparent shadow

       const menuItem = scene.add.text(x, y, text, {
         fontFamily: this.FONTS.family,
         fontSize: size,
         color: Phaser.Display.Color.ValueToColor(color).rgba,
         fontStyle: 'bold',
       }).setOrigin(options.originX ?? 0.5, options.originY ?? 0.5) // Use provided origin or default
         .setInteractive({ useHandCursor: true })
         .setDepth(5);

       // Add hover effects using input events
       menuItem.on('pointerover', () => {
         menuItem.setColor(Phaser.Display.Color.ValueToColor(hoverColor).rgba);
         menuItem.setShadow(0, 0, shadowColor, 8); // Subtle glow
         scene.tweens?.add({ targets: menuItem, scale: 1.05, duration: 100, ease: 'Sine.easeOut' });
       });

       menuItem.on('pointerout', () => {
         menuItem.setColor(Phaser.Display.Color.ValueToColor(color).rgba);
         menuItem.setShadow(0, 0, 'transparent', 0);
         scene.tweens?.add({ targets: menuItem, scale: 1, duration: 100, ease: 'Sine.easeIn' });
       });

       menuItem.on('pointerdown', () => {
         scene.tweens?.add({ targets: menuItem, scale: 0.95, duration: 80, ease: 'Sine.easeOut' });
       });

        menuItem.on('pointerup', (pointer) => {
            // Go back to hover state only if still over
            if (menuItem.getBounds().contains(pointer.x, pointer.y)) {
                scene.tweens?.add({ targets: menuItem, scale: 1.05, duration: 80, ease: 'Sine.easeIn' });
                if (callback) {
                    try { callback(); } catch(e) {
                         console.error("Menu callback error:", e);
                         if (scene.showMessage) scene.showMessage(`Error: ${e.message}`, 2000, true);
                    }
                }
            } else {
                 // If pointer moved off, reset completely
                 menuItem.setColor(Phaser.Display.Color.ValueToColor(color).rgba);
                 menuItem.setShadow(0, 0, 'transparent', 0);
                 menuItem.setScale(1);
            }
        });

       return menuItem;
     } catch (e) { console.error("Error createMenuItem:", e); return null; }
   }

  /** Creates a game tile with solo and team buttons */
    /** Creates a game tile with solo and team buttons */
    static createGameTile(scene, title, x, y, width, height, bgColor, soloCallback, teamCallback, options = {}) {
      if (!scene || !scene.add) {
        console.error("Invalid scene passed to createGameTile");
        return null;
      }
  
      try {
        const container = scene.add.container(x, y);
        container.setSize(width, height); // Set size for positioning
  
        // --- Background Graphic ---
        const bg = scene.add.graphics()
          .fillStyle(0x000000, 0.6) // Slightly transparent black background
          .lineStyle(2, bgColor, 0.9) // Colored border using provided bgColor
          .fillRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm)
          .strokeRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm);
        container.add(bg);
  
        // --- Title Text ---
        const titleText = this.createThemedText(scene, 0, -height / 2 + 30, title, 'sizeLg', bgColor);
        if (titleText) container.add(titleText);
  
        // --- Button Positions and Sizes ---
        const btnY = height / 2 - 35; // Position buttons towards the bottom
        const btnWidth = Math.min(100, width * 0.35); // Calculate button width
        const btnHeight = 35;
        const btnGap = 20; // Gap between buttons (used implicitly by positioning)
        const soloBtnX = -width / 4; // Position left button
        const teamBtnX = width / 4; // Position right button
  
        // --- Solo Button ---
        // Determine options based on whether it's disabled or coming soon
        const soloBtnOptions = {
            bgColor: Phaser.Display.Color.ValueToColor(bgColor).darken(10).color, // Default active color
            textColor: this.COLORS.bgDark, // Default text color
            width: btnWidth, height: btnHeight, fontSize: this.FONTS.sizeSm
        };
        let finalSoloCallback = soloCallback;
        let soloIsDisabled = options.soloDisabled;
  
        if (options.soloComingSoon) { // Handle coming soon state for Solo
            soloBtnOptions.bgColor = this.COLORS.bgLight; // VISUALLY DISABLED: Grey background
            soloBtnOptions.textColor = this.COLORS.textDark; // VISUALLY DISABLED: Dark text
            finalSoloCallback = () => scene.showMessage(options.soloComingSoonMessage || "Solo mode coming soon!"); // Use custom message or default
            soloIsDisabled = false; // Make sure it's clickable
            console.log(`Tile "${title}": Solo button set to 'Coming Soon' state (Clickable Grey).`);
        } else if (options.soloDisabled) { // Handle explicitly disabled state
            soloBtnOptions.bgColor = this.COLORS.bgLight;
            soloBtnOptions.textColor = this.COLORS.textDark;
            finalSoloCallback = options.soloDisabledCallback || (() => {}); // Use disabled callback or no-op
            console.log(`Tile "${title}": Solo button set to 'Disabled' state (Non-Clickable Grey).`);
        } else {
             console.log(`Tile "${title}": Solo button set to 'Enabled' state.`);
        }
  
        // Create the Solo button
        const soloBtn = this.createStyledButton(scene, soloBtnX, btnY, "Solo", finalSoloCallback, soloBtnOptions);
        if (soloBtn) {
            if (soloIsDisabled && !options.soloComingSoon) { // Disable interaction ONLY if explicitly disabled AND not 'coming soon'
                soloBtn.disableInteractive();
            }
            container.add(soloBtn);
        }
  
        // --- Team Button ---
        // Determine options based on disabled/coming soon flags
        const teamBtnOptions = {
            bgColor: Phaser.Display.Color.ValueToColor(bgColor).darken(10).color, // Default active color
            textColor: this.COLORS.bgDark, // Default text color
            width: btnWidth, height: btnHeight, fontSize: this.FONTS.sizeSm
        };
        let finalTeamCallback = teamCallback;
        let teamIsDisabled = options.teamDisabled;
  
        if (options.teamComingSoon) { // Handle coming soon state for Team
            teamBtnOptions.bgColor = this.COLORS.bgLight; // VISUALLY DISABLED: Grey background
            teamBtnOptions.textColor = this.COLORS.textDark; // VISUALLY DISABLED: Dark text
            finalTeamCallback = () => scene.showMessage(options.teamComingSoonMessage || "Team mode coming soon!"); // Use custom message or default
            teamIsDisabled = false; // Make sure it's clickable
            console.log(`Tile "${title}": Team button set to 'Coming Soon' state (Clickable Grey).`);
        } else if (options.teamDisabled) { // Handle explicitly disabled state
            teamBtnOptions.bgColor = this.COLORS.bgLight;
            teamBtnOptions.textColor = this.COLORS.textDark;
            finalTeamCallback = options.teamDisabledCallback || (() => {}); // Use disabled callback or no-op
            console.log(`Tile "${title}": Team button set to 'Disabled' state (Non-Clickable Grey).`);
        } else {
             console.log(`Tile "${title}": Team button set to 'Enabled' state.`);
        }
  
        // Create the Team button
        const teamBtn = this.createStyledButton(scene, teamBtnX, btnY, "Team", finalTeamCallback, teamBtnOptions);
        if (teamBtn) {
            // Disable interaction ONLY if explicitly disabled AND NOT 'coming soon'
            if (teamIsDisabled && !options.teamComingSoon) {
                 teamBtn.disableInteractive();
            }
            container.add(teamBtn);
        }
  
                // --- Toggle Button (if hasToggle is true) ---
        if (options.hasToggle && options.toggleLabel && options.onToggle) {
            const toggleY = -height / 2 + 58; // Position below title
            const toggleBtnOptions = {
                bgColor: this.COLORS.accent4, // Lime/green for toggle
                textColor: this.COLORS.bgDark,
                width: Math.min(100, width * 0.5),
                height: 22,
                fontSize: '12px'
            };

            const toggleBtn = this.createStyledButton(
                scene, 
                0, // Center horizontally
                toggleY, 
                options.toggleLabel, 
                () => {
                    // Call the toggle callback
                    if (typeof options.onToggle === 'function') {
                        options.onToggle();
                        // Update the button text after toggle
                        setTimeout(() => {
                            if (toggleBtn && toggleBtn.list && toggleBtn.list[1]) {
                                // Get updated label from the scene
                                const newLabel = options.getUpdatedLabel ? options.getUpdatedLabel() : options.toggleLabel;
                                toggleBtn.list[1].setText(newLabel);
                            }
                        }, 100);
                    }
                }, 
                toggleBtnOptions
            );

            if (toggleBtn) {
                container.add(toggleBtn);
                // Store reference for updates
                container.setData('toggleBtn', toggleBtn);
            }
        }

        // Store data for potential resizing/recreation
        container.setData('title', title);
        container.setData('color', bgColor);
        container.setData('soloCallback', soloCallback);
        container.setData('teamCallback', teamCallback);
        container.setData('type', 'gameTile');
        container.setData('soloDisabled', options.soloDisabled);
        container.setData('teamDisabled', options.teamDisabled);
        container.setData('soloComingSoon', options.soloComingSoon);
        container.setData('teamComingSoon', options.teamComingSoon);
        container.setData('soloDisabledCallback', options.soloDisabledCallback);
        container.setData('teamDisabledCallback', options.teamDisabledCallback);
        container.setData('soloComingSoonMessage', options.soloComingSoonMessage);
        container.setData('teamComingSoonMessage', options.teamComingSoonMessage);
        // Toggle-related data
        container.setData('hasToggle', options.hasToggle);
        container.setData('toggleLabel', options.toggleLabel);
        container.setData('onToggle', options.onToggle);

        return container;
      } catch (e) {
        console.error("Error in createGameTile:", e);
        return null;
      }
    }

  
/** Creates a utility tile with one main action */
static createUtilityTile(scene, title, x, y, width, height, color, callback) {
  if (!scene || !scene.add) {
    console.error("Invalid scene passed to createUtilityTile");
    return null;
  }

  try {
    const container = scene.add.container(x, y);
    container.setSize(width, height); // Set container size for interaction

    // Background graphic
    const bg = scene.add.graphics()
      .fillStyle(0x000000, 0.6)
      .lineStyle(2, color, 0.9)
      .fillRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm)
      .strokeRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm);
     container.add(bg);

    // Make container interactive
    container.setInteractive({ useHandCursor: true });

    // Hover effects using Graphics state
    const hoverColor = Phaser.Display.Color.ValueToColor(color).lighten(15).color;
    const normalColor = color;
    const normalFill = 0x000000;
    const hoverFill = 0x111111;

     container.on('pointerover', () => {
         bg.clear()
           .fillStyle(hoverFill, 0.7)
           .lineStyle(2, hoverColor, 1.0)
           .fillRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm)
           .strokeRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm);
         scene.tweens?.add({targets: container, scale: 1.02, duration: 100});
     });

     container.on('pointerout', () => {
         bg.clear()
           .fillStyle(normalFill, 0.6)
           .lineStyle(2, normalColor, 0.9)
           .fillRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm)
           .strokeRoundedRect(-width / 2, -height / 2, width, height, this.SPACING.sm);
         scene.tweens?.add({targets: container, scale: 1, duration: 100});
     });


    // Click handler
    container.on('pointerdown', () => {
       scene.tweens?.add({ targets: container, scale: 0.98, duration: 80, ease: 'Sine.easeOut' });
       if (typeof callback === 'function') {
           try { callback(); } catch (e) {
                console.error(`Error executing callback for tile "${title}":`, e);
                 if (scene.showMessage) scene.showMessage(`Error: ${e.message}`, 2000, true);
           }
       }
    });
     container.on('pointerup', () => { // Reset scale on pointer up
         scene.tweens?.add({ targets: container, scale: 1.02, duration: 80, ease: 'Sine.easeIn' }); // Back to hover scale if still over
     });


    // Title text
    const titleText = this.createThemedText(scene, 0, 0, title, 'sizeLg', color, 0.5);
    if(titleText) container.add(titleText);

    // Store data for resizing and recreation
    container.setData('title', title);
    container.setData('color', color);
    container.setData('utilityCallback', callback);
    container.setData('type', 'utilityTile');

    return container;
  } catch (e) {
    console.error("Error in createUtilityTile:", e);
    return null;
  }
}


  /** Shows a temporary message overlay within the Phaser scene. */
  static showMessage(scene, message, duration = 2000, isError = false) {
    if (!scene || !scene.add || !scene.cameras?.main || !scene.scene?.systems?.scenePlugin?.isActive(scene.scene.key)) {
        console.warn("Invalid scene or scene not active for showMessage");
        return null;
    }
    try {
      const width = scene.cameras.main.width;
      const height = scene.cameras.main.height;
      const msgBgColor = isError ? 0x550000 : 0x003300; // Dark Red or Dark Green
      const msgTextColor = isError ? this.COLORS.danger : this.COLORS.primary;
      const yPos = 50; // Position near top

      const container = scene.add.container(width / 2, yPos - 20).setDepth(10000).setAlpha(0); // Start slightly above and faded out

      // Use a temporary text object to estimate size for the background
      const tempText = scene.add.text(0, 0, message, {
        fontFamily: this.FONTS.family,
        fontSize: this.FONTS.sizeMd,
        wordWrap: { width: Math.min(width * 0.8, 450) } // Limit wrap width
      }).setVisible(false);
      const textWidth = Math.max(150, tempText.width + this.SPACING.lg * 2); // Ensure min width + padding
      const textHeight = tempText.height + this.SPACING.md * 2;
      tempText.destroy(); // Clean up temp object

      // Create background graphics
      const bg = scene.add.graphics()
          .fillStyle(msgBgColor, 0.9)
          .fillRoundedRect(-textWidth / 2, -textHeight / 2, textWidth, textHeight, this.SPACING.xs);
      container.add(bg);

      // Create actual text, centered within the background
      const text = scene.add.text(0, 0, message, {
        fontFamily: this.FONTS.family,
        fontSize: this.FONTS.sizeMd,
        color: Phaser.Display.Color.ValueToColor(msgTextColor).rgba,
        align: 'center',
        wordWrap: { width: textWidth - this.SPACING.md } // Wrap width inside padding
      }).setOrigin(0.5);
       container.add(text);

      // Fade in and then out
      scene.tweens.add({
        targets: container,
        alpha: 1,
        y: yPos, // Move down to final position
        duration: 300,
        ease: 'Power2',
        onComplete: () => {
          // Check if scene/container still valid before setting timer
          if(container.scene && container.active) {
            const timer = scene.time.delayedCall(duration, () => {
              // Check again before fading out
              if(container.scene && container.active) {
                scene.tweens.add({
                  targets: container,
                  alpha: 0,
                  y: yPos - 10, // Move back up slightly
                  duration: 300,
                  ease: 'Power2',
                  onComplete: () => {
                     // Final check before destroy
                     if (container.active) container.destroy();
                  }
                });
              }
            });
            // Store timer reference on container for potential cleanup
            container.setData('hideTimer', timer);
          } else if (container.active) {
               // If scene became inactive before timer started, destroy immediately
               container.destroy();
          }
        }
      });

      return container;
    } catch (e) { console.error("Error showMessage:", e); return null; }
  }

  // --------------------------------------------------------------------
  // --- Background Generation Methods (Centralized Here) ---
  // --------------------------------------------------------------------

  /**
   * Creates a professional, consistent background for any scene
   * @param {Phaser.Scene} scene - The scene to add the background to
   * @param {Object} options - Background configuration options
   * @returns {Object} References to created background elements
   */
  static createProfessionalBackground(scene, options = {}) {
    if (!scene || !scene.add) {
      console.error("Invalid scene passed to createProfessionalBackground");
      return {};
    }

    try {
      const width = scene.cameras?.main?.width || 800;
      const height = scene.cameras?.main?.height || 600;

      // Default options merged with provided options
      const config = {
        backgroundColor: "#050010", // Very dark blue-black
        gridColor: 0x0033AA,
        gridAlpha: 0.08,
        gridSize: 60,
        particleCount: 25,
        particleColors: [0x00ffff, 0x00bbff, 0x00ff88, 0x88ff00], // Cyan, blue, green, lime
        particleMinSize: 1,
        particleMaxSize: 3,
        particleMinAlpha: 0.05,
        particleMaxAlpha: 0.2,
        particleMinDuration: 8000,
        particleMaxDuration: 20000,
        addDiagonalLines: true,
        depth: -10,
        gradientTopColor: 0x000033,
        gradientBottomColor: 0x003366,
        ...options // User options override defaults
      };

      // Set camera background to specified color
      if (scene.cameras?.main) {
        scene.cameras.main.setBackgroundColor(config.backgroundColor);
      }

      // Create grid background
      const bgGrid = scene.add.graphics({ lineStyle: { width: 1, color: config.gridColor, alpha: config.gridAlpha } }).setDepth(config.depth);

      // Draw grid lines (Function to redraw on resize)
      const drawGrid = (graphics, w, h, size, addDiagonals) => {
          graphics.clear();
          graphics.lineStyle(1, config.gridColor, config.gridAlpha);
          for (let x = 0; x < w + size; x += size) graphics.strokeLineShape(new Phaser.Geom.Line(x, 0, x, h));
          for (let y = 0; y < h + size; y += size) graphics.strokeLineShape(new Phaser.Geom.Line(0, y, w, y));
          if (addDiagonals) {
              for (let i = -h; i < w + h; i += size * 2) graphics.strokeLineShape(new Phaser.Geom.Line(i, 0, i + h, h)); // Diagonal 1
              for (let i = -h; i < w + h; i += size * 2) graphics.strokeLineShape(new Phaser.Geom.Line(i, h, i + h, 0)); // Diagonal 2
          }
      };
      drawGrid(bgGrid, width, height, config.gridSize, config.addDiagonalLines);

  // Create floating particles
  const particles = [];
  for (let i = 0; i < config.particleCount; i++) {
    const x = Phaser.Math.Between(0, width);
    const y = Phaser.Math.Between(0, height);
    const size = Phaser.Math.Between(config.particleMinSize, config.particleMaxSize);
    const color = Phaser.Utils.Array.GetRandom(config.particleColors);
    const alpha = Phaser.Math.FloatBetween(config.particleMinAlpha, config.particleMaxAlpha);

    const particle = scene.add.circle(x, y, size, color, alpha).setDepth(config.depth + 1);
    particles.push(particle);

    // Add floating animation (will self-manage position on restart)
    const tween = scene.tweens.add({
      targets: particle,
      y: () => particle.y - Phaser.Math.Between(100, 300), // Use function for dynamic target
      alpha: 0,
      duration: () => Phaser.Math.Between(config.particleMinDuration, config.particleMaxDuration),
      ease: 'Sine.easeInOut',
      delay: Phaser.Math.Between(0, 5000), // Random start delay
      onComplete: () => {
         if (!particle.active || !particle.scene) return; // Guard if destroyed
         const currentHeight = particle.scene.cameras.main.height;
         const currentWidth = particle.scene.cameras.main.width;
         particle.y = currentHeight + Phaser.Math.Between(10, 30);
         particle.x = Phaser.Math.Between(0, currentWidth);
         particle.alpha = Phaser.Math.FloatBetween(config.particleMinAlpha, config.particleMaxAlpha);
         tween.restart(); // Restart the tween
      }
    });
     particle.setData('tween', tween); // Store tween reference for cleanup
  }

  // Add subtle glow gradient at bottom (RenderTexture for efficiency)
  let gradientTexture = scene.add.renderTexture(0, 0, width, height * 0.4).setOrigin(0, 0).setDepth(config.depth - 1);
  const drawGradient = (rt, w, h, topColorHex, bottomColorHex) => {
       if(!rt.active) return; // Guard if destroyed
       const gradientHeight = h * 0.4;
       rt.resize(w, gradientHeight);
       rt.setPosition(0, h - gradientHeight); // Position at bottom

       const ctx = rt.context; // Use 2D context for gradient
       const gradient = ctx.createLinearGradient(0, 0, 0, gradientHeight);
       const topColor = Phaser.Display.Color.ValueToColor(topColorHex);
       const bottomColor = Phaser.Display.Color.ValueToColor(bottomColorHex);

       gradient.addColorStop(0, `rgba(${topColor.r},${topColor.g},${topColor.b},0)`); // Transparent top
       gradient.addColorStop(1, `rgba(${bottomColor.r},${bottomColor.g},${bottomColor.b},0.15)`); // Subtle bottom

       rt.clear(); // Clear previous render
       ctx.fillStyle = gradient;
       ctx.fillRect(0, 0, w, gradientHeight);
       rt.save(); // Save the texture state
  };
   drawGradient(gradientTexture, width, height, config.gradientTopColor, config.gradientBottomColor);

  // Store helper functions on elements for resize
  bgGrid.setData('drawGrid', drawGrid);
  gradientTexture.setData('drawGradient', drawGradient);

  // Return references
  return {
    grid: bgGrid,
    particles: particles,
    gradient: gradientTexture
  };
} catch (e) {
  console.error("Error in createProfessionalBackground:", e);
  return {};
}
}

/** Updates the professional background when the scene resizes */
static updateProfessionalBackground(scene, backgroundElements, options = {}) {
 if (!scene || !scene.cameras?.main || !backgroundElements) return;

 const width = scene.cameras.main.width;
 const height = scene.cameras.main.height;
 const config = { gridSize: 60, addDiagonalLines: true, gradientTopColor: 0x000033, gradientBottomColor: 0x003366, ...options };

 // Redraw grid
 if (backgroundElements.grid && backgroundElements.grid.active) {
     const drawGrid = backgroundElements.grid.getData('drawGrid');
     if(drawGrid) drawGrid(backgroundElements.grid, width, height, config.gridSize, config.addDiagonalLines);
 }

 // Redraw gradient
 if (backgroundElements.gradient && backgroundElements.gradient.active) {
     const drawGradient = backgroundElements.gradient.getData('drawGradient');
      if(drawGradient) drawGradient(backgroundElements.gradient, width, height, config.gradientTopColor, config.gradientBottomColor);
 }

 // Particles will reposition themselves via their tween logic
}

/** Cleans up professional background resources */
static cleanupProfessionalBackground(backgroundElements) {
 if (backgroundElements.grid) { try { backgroundElements.grid.destroy(); } catch(e){} }
 if (backgroundElements.gradient) { try { backgroundElements.gradient.destroy(); } catch(e){} }
 if (backgroundElements.particles) {
   backgroundElements.particles.forEach(particle => {
     try {
        const tween = particle.getData('tween');
        if (tween) tween.remove(); // Remove tween first
        particle.destroy();
     } catch(e){}
   });
 }
}

/** Creates an AI-themed background with code/data visualization feel */
static createAIThemedBackground(scene, options = {}) {
if (!scene || !scene.add) {
  console.error("Invalid scene passed to createAIThemedBackground");
  return {};
}

try {
  const width = scene.cameras?.main?.width || 800;
  const height = scene.cameras?.main?.height || 600;

  const config = {
    backgroundColor: "#080818", // Dark blue-black default
    gridColor: 0x00DD77,       // AI Green default
    codeColor: 0x00DDAA,       // Lighter AI Green default
    gridAlpha: 0.08,
    codeAlpha: 0.06,
    gridSize: 60,
    particleCount: 20,
    codeElementCount: 30,
    dataFlowCount: 5,
    depth: -10,
    ...options
  };

  // Set camera background
  if (scene.cameras?.main) scene.cameras.main.setBackgroundColor(config.backgroundColor);

  // Create grid background
  const bgGrid = scene.add.graphics({ lineStyle: { width: 1, color: config.gridColor, alpha: config.gridAlpha } }).setDepth(config.depth);
  const drawGrid = (graphics, w, h, size) => {
      graphics.clear().lineStyle(1, config.gridColor, config.gridAlpha);
      for (let x = 0; x < w + size; x += size) graphics.strokeLineShape(new Phaser.Geom.Line(x, 0, x, h));
      for (let y = 0; y < h + size; y += size) graphics.strokeLineShape(new Phaser.Geom.Line(0, y, w, y));
  };
  drawGrid(bgGrid, width, height, config.gridSize);
  bgGrid.setData('drawGrid', drawGrid);

  // Add "code-like" text elements
  const codeElements = [];
  const codeStrings = ["01", "10", "fn()", "{}", "=>", "ai.run", "data", "null", "true", "false", "||", "&&", "++", "--", "{}", "[]", "<>", "::"];
  for (let i = 0; i < config.codeElementCount; i++) {
      const codeString = Phaser.Utils.Array.GetRandom(codeStrings);
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height);
      const size = Phaser.Math.Between(10, 16);
      const codeText = scene.add.text(x, y, codeString, {
          fontFamily: 'monospace', fontSize: `${size}px`,
          color: Phaser.Display.Color.ValueToColor(config.codeColor).rgba,
          alpha: config.codeAlpha
      }).setOrigin(0.5).setDepth(config.depth);
      codeElements.push(codeText);

      const tween = scene.tweens.add({
          targets: codeText, alpha: { from: config.codeAlpha, to: Phaser.Math.FloatBetween(0.01, config.codeAlpha * 0.5) },
          duration: Phaser.Math.Between(4000, 10000), yoyo: true, repeat: -1
      });
      codeText.setData('tween', tween);
  }

  // Add floating particles like data points
  const particles = [];
  const particleColors = [0x00FFAA, 0x00AAFF, 0x88FF00, 0xFFAA00]; // Teal, Blue, Lime, Orange
  for (let i = 0; i < config.particleCount; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height);
      const size = Phaser.Math.Between(1, 3);
      const color = Phaser.Utils.Array.GetRandom(particleColors);
      const alpha = Phaser.Math.FloatBetween(0.1, 0.3);
      const particle = scene.add.circle(x, y, size, color, alpha).setDepth(config.depth + 1);
      particles.push(particle);

      const tween = scene.tweens.add({
          targets: particle,
          x: () => particle.x + Phaser.Math.Between(-50, 50), // Use function for dynamic target
          y: () => particle.y + Phaser.Math.Between(-50, 50),
          alpha: () => Phaser.Math.FloatBetween(0.05, 0.2),
          duration: Phaser.Math.Between(8000, 15000), ease: 'Sine.easeInOut', yoyo: true, repeat: -1
      });
       particle.setData('tween', tween);
  }

  // Add "data flow" elements - moving dots along lines
  const dataFlows = [];
  for (let i = 0; i < config.dataFlowCount; i++) {
      const startX = Phaser.Math.Between(0, width);
      const startY = Phaser.Math.Between(0, height);
      const endX = Phaser.Math.Between(0, width);
      const endY = Phaser.Math.Between(0, height);

      const flowLine = scene.add.graphics().lineStyle(1, config.gridColor, 0.05)
         .strokeLineShape(new Phaser.Geom.Line(startX, startY, endX, endY)).setDepth(config.depth);
      dataFlows.push(flowLine);

      const flowDot = scene.add.circle(startX, startY, 2, 0x00FFFF, 0.5).setDepth(config.depth + 2); // Cyan dot
      dataFlows.push(flowDot);

      const tween = scene.tweens.add({
          targets: flowDot, x: endX, y: endY, alpha: 0, // Fade out as it reaches end
          duration: Phaser.Math.Between(4000, 8000), ease: 'Linear', repeat: -1, repeatDelay: Phaser.Math.Between(1000, 3000),
          onRepeat: function() { // Use function scope for 'this'
               if (!flowDot.active || !flowDot.scene) { tween.remove(); return; } // Guard
               flowDot.setPosition(startX, startY).setAlpha(0.5); // Reset position and alpha
          }
      });
       flowDot.setData('tween', tween);
       flowLine.setData('dotTween', tween); // Store ref on line too for cleanup
  }

  return { grid: bgGrid, particles, codeElements, dataFlows };
} catch (e) {
  console.error("Error in createAIThemedBackground:", e);
  return {};
}
}

/** Updates the AI-themed background when the scene resizes */
static updateAIThemedBackground(scene, backgroundElements, options = {}) {
   if (!scene || !scene.cameras?.main || !backgroundElements) return;
   const width = scene.cameras.main.width;
   const height = scene.cameras.main.height;
   const config = { gridSize: 60, ...options };

   // Redraw grid
   if (backgroundElements.grid && backgroundElements.grid.active) {
        const drawGrid = backgroundElements.grid.getData('drawGrid');
        if(drawGrid) drawGrid(backgroundElements.grid, width, height, config.gridSize);
   }

   // Reposition random elements (code, particles) - might be simpler to recreate
   // For now, let tweens handle repositioning over time

   // Recreate data flows for new bounds? Or just let them run off screen?
   // For simplicity, let existing flows continue. New flows aren't created on resize.
}

/** Cleans up AI-themed background resources */
static cleanupAIThemedBackground(backgroundElements) {
 const cleanup = (elements) => {
     if (!elements) return;
     elements.forEach(el => {
         try {
            const tween = el.getData?.('tween') || el.getData?.('dotTween'); // Check both data keys
            if (tween) tween.remove();
            el.destroy();
         } catch(e) {}
     });
 };

 if (backgroundElements.grid) try { backgroundElements.grid.destroy(); } catch(e){}
 cleanup(backgroundElements.particles);
 cleanup(backgroundElements.codeElements);
 cleanup(backgroundElements.dataFlows);
}

}

export default MenuUIUtils;